import DateTimePicker from '@react-native-community/datetimepicker';
import type { DateTimePickerEvent } from '@react-native-community/datetimepicker';
import React, { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';

import { HelperText, Text } from '@/components/ui';

interface DateInputProps {
	value: Date | null;
	onChange: (date: Date | null) => void;
	label: string;
	placeholder?: string;
	error?: string | boolean;
	disabled?: boolean;
	mode?: 'date' | 'datetime' | 'time';
	style?: React.CSSProperties;
	testID?: string;
	/**
	 * The value is a business-date marker whose calendar day lives in its UTC fields (what the
	 * web branch emits). Makes the NATIVE picker honour that contract too. Opt-in: some callers
	 * still hand the native picker device-local dates.
	 */
	utcDay?: boolean;
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

const isoFromDate = (d: Date | null): string => {
	if (!d) return '';
	return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};

const dateFromIso = (iso: string): Date | null => {
	const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!m) return null;
	return new Date(`${iso}T05:00:00Z`);
};

/**
 * Format a digit-only string into a partial MM/DD/YYYY display.
 * Accepts 0-8 digits; inserts slashes after positions 2 and 4. A 6-digit
 * trailing year is expanded to 20YY when complete.
 */
const formatDigitsToDisplay = (digits: string): string => {
	const d = digits.replace(/\D/g, '').slice(0, 8);
	if (d.length === 0) return '';
	if (d.length <= 2) return d;
	if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
	return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

/**
 * Try to parse a digit-only string to ISO YYYY-MM-DD. Returns '' until
 * the user has entered enough digits to disambiguate (8, or 6 with YY-as-20YY).
 */
const parseDigitsToIso = (digits: string): string => {
	const d = digits.replace(/\D/g, '');
	let mm = '', dd = '', yyyy = '';
	if (d.length === 8) {
		mm = d.slice(0, 2); dd = d.slice(2, 4); yyyy = d.slice(4, 8);
	} else if (d.length === 6) {
		mm = d.slice(0, 2); dd = d.slice(2, 4); yyyy = `20${d.slice(4, 6)}`;
	} else {
		return '';
	}
	const month = Number(mm);
	const day = Number(dd);
	const year = Number(yyyy);
	if (!Number.isFinite(month) || month < 1 || month > 12) return '';
	if (!Number.isFinite(day) || day < 1 || day > 31) return '';
	if (!Number.isFinite(year) || year < 1900 || year > 2999) return '';
	return `${yyyy}-${mm}-${dd}`;
};

export const DateInput: React.FC<DateInputProps> = ({
	value,
	onChange,
	label,
	placeholder = 'MM/DD/YYYY',
	error,
	disabled = false,
	mode = 'date',
	style,
	testID,
	utcDay = false,
}) => {
	const [isFocused, setIsFocused] = useState(false);
	const pickerRef = useRef<HTMLInputElement>(null);
	const [digits, setDigits] = useState<string>(() => {
		if (!value) return '';
		const iso = isoFromDate(value);
		const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		return m ? `${m[2]}${m[3]}${m[1]}` : '';
	});

	// Re-sync from prop when parent updates value externally. Two safety nets:
	//   1. Depend on the ISO string, not the Date reference — many parents
	//      construct `new Date(...)` inline, which would otherwise refire this
	//      effect every render.
	//   2. Skip while focused. Once the user types 6 digits parseDigitsToIso
	//      commits a `20YY` year to the parent; if we resync immediately the
	//      4-digit year would overwrite their in-progress typing and trap them
	//      at the auto-completed year. Authoritative local state while typing,
	//      reconcile on blur or external (non-typing) updates.
	const valueIso = value ? isoFromDate(value) : '';
	useEffect(() => {
		if (isFocused) return;
		if (!valueIso) {
			if (digits !== '') setDigits('');
			return;
		}
		const m = valueIso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (!m) return;
		const next = `${m[2]}${m[3]}${m[1]}`;
		if (next !== digits) setDigits(next);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [valueIso, isFocused]);

	// The native picker renders and returns DEVICE-LOCAL fields. For a `utcDay` value, translate
	// both ways so a Pacific device neither shows the 05:00Z marker as the previous day nor
	// emits a pick whose UTC day is tomorrow.
	const translateUtcDay = utcDay && mode === 'date';
	const nativeValue = translateUtcDay && value
		? new Date(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate(), 12)
		: value || new Date();

	const handleDateChange = (_event: DateTimePickerEvent, selectedDate?: Date) => {
		if (selectedDate === undefined) return;
		if (!translateUtcDay) {
			onChange(selectedDate);
			return;
		}
		onChange(new Date(Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 5)));
	};

	if (Platform.OS === 'web') {
		const borderColor = error ? '#d32f2f' : (isFocused ? '#2196f3' : '#e0e0e0');
		const display = formatDigitsToDisplay(digits);
		const pickerIso = value ? isoFromDate(value) : '';
		const openPicker = () => {
			const el = pickerRef.current;
			if (!el || disabled) return;
			// showPicker is the modern API; fall back to focus+click for older browsers.
			const anyEl = el as HTMLInputElement & { showPicker?: () => void };
			if (typeof anyEl.showPicker === 'function') anyEl.showPicker();
			else { el.focus(); el.click(); }
		};
		return (
			<View testID={testID} style={{ marginVertical: 12 }}>
				<Text style={{
					marginBottom: 6,
					fontSize: 14,
					fontWeight: '600',
					color: error ? '#d32f2f' : '#424242',
					textTransform: 'uppercase',
					letterSpacing: 0.5,
				}}
				>
					{label}
				</Text>
				<div style={{ position: 'relative', width: '100%' }}>
					<input
						type="text"
						inputMode="numeric"
						value={display}
						onChange={(e) => {
							const next = e.target.value.replace(/\D/g, '').slice(0, 8);
							setDigits(next);
							const iso = parseDigitsToIso(next);
							if (iso) {
								onChange(dateFromIso(iso));
							} else if (next.length === 0) {
								onChange(null);
							}
						}}
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						placeholder={placeholder}
						disabled={disabled}
						maxLength={10}
						style={{
							width: '100%',
							boxSizing: 'border-box',
							backgroundColor: '#ffffff',
							color: display ? '#212121' : '#959595',
							fontSize: 16,
							padding: 16,
							paddingRight: 44,
							borderRadius: 8,
							minHeight: 56,
							border: `1px solid ${borderColor}`,
							boxShadow: isFocused ? '0 0 0 2px rgba(33,150,243,0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
							outline: 'none',
							transition: 'border-color 150ms ease, box-shadow 150ms ease',
							opacity: disabled ? 0.6 : 1,
							cursor: disabled ? 'not-allowed' : 'text',
							margin: 0,
							fontFamily: 'monospace',
							letterSpacing: 0.5,
							...style,
						}}
					/>
					<button
						type="button"
						aria-label="Open calendar"
						onClick={openPicker}
						disabled={disabled}
						tabIndex={-1}
						style={{
							position: 'absolute',
							right: 8,
							top: '50%',
							transform: 'translateY(-50%)',
							width: 32,
							height: 32,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							background: 'transparent',
							border: 'none',
							borderRadius: 4,
							cursor: disabled ? 'not-allowed' : 'pointer',
							color: '#666',
							padding: 0,
							fontSize: 18,
						}}
					>
						{/* eslint-disable-next-line react-native/no-raw-text */}
						<span aria-hidden="true">📅</span>
					</button>
					<input
						ref={pickerRef}
						type="date"
						value={pickerIso}
						onChange={(e) => {
							const iso = e.target.value;
							if (!iso) { setDigits(''); onChange(null); return; }
							const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
							if (!m) return;
							setDigits(`${m[2]}${m[3]}${m[1]}`);
							onChange(dateFromIso(iso));
						}}
						tabIndex={-1}
						aria-hidden="true"
						style={{
							position: 'absolute',
							right: 8,
							top: '50%',
							width: 1,
							height: 1,
							opacity: 0,
							pointerEvents: 'none',
							border: 'none',
							padding: 0,
							margin: 0,
						}}
					/>
				</div>
				{typeof error === 'string' && error && (
					<HelperText type="error" visible={!!error}>
						{error}
					</HelperText>
				)}
			</View>
		);
	}

	return (
		<View testID={testID}>
			<HelperText type="info" visible={true}>
				{label}
			</HelperText>
			<DateTimePicker
				disabled={disabled}
				value={nativeValue}
				mode={mode}
				is24Hour={true}
				display="default"
				onChange={handleDateChange}
			/>
			{typeof error === 'string' && error && (
				<HelperText type="error" visible={!!error}>
					{error}
				</HelperText>
			)}
		</View>
	);
};
